import React, { useState, useEffect, useRef } from 'react';

/**
 * ProgressiveImage — LQIP-style progressive loading
 *
 * Shows a blurred shimmer placeholder while the image is loading,
 * then fades in the full-res image with a blur-to-sharp transition.
 * Uses IntersectionObserver for true lazy loading (only starts fetching
 * when the image enters the viewport).
 */
function ProgressiveImage({ src, alt, className, onClick, masonry }) {
  const [status, setStatus] = useState('idle'); // idle | loading | loaded | error
  const [thumbSrc, setThumbSrc] = useState(null); // blurred canvas thumbnail
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const observerRef = useRef(null);

  // Generate a tiny blurred canvas thumbnail from the loaded image
  const generateThumb = (img) => {
    try {
      const THUMB_SIZE = 20;
      const canvas = document.createElement('canvas');
      canvas.width = THUMB_SIZE;
      canvas.height = THUMB_SIZE;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, THUMB_SIZE, THUMB_SIZE);
      setThumbSrc(canvas.toDataURL('image/jpeg', 0.3));
    } catch {
      // Cross-origin canvas taint — skip thumbnail, just use shimmer
    }
  };

  useEffect(() => {
    if (!src) return;

    // Use IntersectionObserver to lazy-start loading
    const el = containerRef.current;
    if (!el) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observerRef.current?.disconnect();
          setStatus('loading');

          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            generateThumb(img);
            setStatus('loaded');
          };
          img.onerror = () => setStatus('error');
          img.src = src;
        }
      },
      { rootMargin: '200px' } // start loading 200px before entering viewport
    );
    observerRef.current.observe(el);

    return () => observerRef.current?.disconnect();
  }, [src]);

  const isLoaded = status === 'loaded';

  // Masonry mode: let image determine its own height naturally
  if (masonry) {
    return (
      <div
        ref={containerRef}
        className={`prog-img-container prog-img-masonry ${className || ''}`}
        onClick={onClick}
        style={{ position: 'relative', width: '100%', display: 'block' }}
      >
        {/* Shimmer placeholder with fixed aspect while loading */}
        {!isLoaded && (
          <div
            className="prog-img-skeleton skeleton"
            style={{ width: '100%', paddingBottom: '100%', position: 'relative' }}
          />
        )}

        {/* Full-res image — natural height, fades in */}
        {status !== 'idle' && (
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            loading="lazy"
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              opacity: isLoaded ? 1 : 0,
              filter: isLoaded ? 'blur(0px)' : 'blur(8px)',
              transition: 'opacity 0.5s ease, filter 0.5s ease',
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`prog-img-container ${className || ''}`}
      onClick={onClick}
      style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}
    >
      {/* Shimmer skeleton — always rendered, hidden when image loads */}
      <div
        className="prog-img-skeleton skeleton"
        style={{
          position: 'absolute',
          inset: 0,
          opacity: isLoaded ? 0 : 1,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none',
        }}
      />

      {/* Blurred thumbnail (LQIP) — shown while full image loads */}
      {thumbSrc && !isLoaded && (
        <img
          src={thumbSrc}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'blur(12px)',
            transform: 'scale(1.1)', // prevent blur edges showing
            opacity: 0.8,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Full-res image — fades in when loaded */}
      {status !== 'idle' && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          loading="lazy"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: isLoaded ? 1 : 0,
            filter: isLoaded ? 'blur(0px)' : 'blur(8px)',
            transform: isLoaded ? 'scale(1)' : 'scale(1.03)',
            transition: 'opacity 0.5s ease, filter 0.5s ease, transform 0.5s ease',
          }}
        />
      )}
    </div>
  );
}

export default ProgressiveImage;
